import type {DefaultMantineColor} from '@mantine/core';
import {
  ComplianceStateEnum,
  CourseAssignmentStateEnum,
  CourseClassroomAttendanceEnum,
  CourseClassroomAttendeeResultEnum,
  CourseClassroomStateEnum,
  DeficiencyResolutionSourceEnum,
  DeficiencyStatusEnum,
  InspectionStateEnum,
  RiskAssessmentCodeAreaEnum,
  SafetyEventStateEnum,
  TaskDefinitionStateEnum,
  UserStatusEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';

// Structurally identical to design/atoms/Pill's PillProps; declared locally
// since src/shared sits below the design tier.
export interface PillProps {
  text: string;
  color?: DefaultMantineColor;
}

export const YesNoPills = (val?: boolean): PillProps => {
  if (val === true) {
    return {
      text: 'Yes',
      color: 'green',
    };
  }

  if (val === false) {
    return {
      text: 'No',
      color: 'yellow',
    };
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};
export const NoYesPills = (val?: boolean): PillProps => {
  if (val === false) {
    return {
      text: 'No',
      color: 'green',
    };
  }

  if (val === true) {
    return {
      text: 'Yes',
      color: 'yellow',
    };
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const ActiveInactivePills = (active?: boolean): PillProps => {
  if (active === true) {
    return {
      text: 'Active',
      color: 'green',
    };
  }

  if (active === false) {
    return {
      text: 'Inactive',
      color: 'yellow',
    };
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const UserStatePills = (state?: UserStatusEnum): PillProps => {
  if (state === UserStatusEnum.Active) {
    return {
      text: 'Active',
      color: 'green',
    };
  }

  if (state === UserStatusEnum.Inactive) {
    return {
      text: 'Inactive',
      color: 'yellow',
    };
  }

  if (state === UserStatusEnum.Pending) {
    return {
      text: 'Pending',
      color: 'blue',
    };
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const UserTypePills = (type?: UserTypeEnum): PillProps => {
  if (type === UserTypeEnum.Employee) {
    return {
      text: 'Employee',
      color: 'blue',
    };
  }

  if (type === UserTypeEnum.Contractor) {
    return {
      text: 'Contractor',
      color: 'yellow',
    };
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const IdpManagedPill: PillProps = {
  text: 'Managed by identity provider',
  color: 'blue',
};

export const UserSuspendedPill: PillProps = {
  text: 'Suspended',
  color: 'red',
};
export const UserLeaveOfAbsencePill: PillProps = {
  text: 'LOA',
  color: 'red',
};

export const UserIneligiblePill: PillProps = {
  text: 'Ineligible',
  color: 'red',
};

export const UserBannedPill: PillProps = {
  text: 'Banned',
  color: 'red',
};

export const CourseClassroomStatePill = (
  state?: CourseClassroomStateEnum
): PillProps => {
  switch (state) {
    case CourseClassroomStateEnum.InProgress:
      return {text: 'In-progress', color: 'blue'};
    case CourseClassroomStateEnum.Completed:
      return {text: 'Complete', color: 'green'};
    case CourseClassroomStateEnum.Cancelled:
      return {text: 'Cancelled', color: 'red'};
    case CourseClassroomStateEnum.NotStarted:
      return {text: 'Not Started', color: 'yellow'};
    case undefined:
    case CourseClassroomStateEnum.Invalid:
    default:
      break;
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const CourseClassroomAttendancePill = (
  attendance?: CourseClassroomAttendanceEnum
): PillProps => {
  switch (attendance) {
    case CourseClassroomAttendanceEnum.Present:
      return {text: 'Present', color: 'green'};
    case CourseClassroomAttendanceEnum.Absent:
      return {text: 'Absent', color: 'red'};
    case CourseClassroomAttendanceEnum.Excused:
      return {text: 'Excused', color: 'yellow'};
    case CourseClassroomAttendanceEnum.Registered:
    case undefined:
    case CourseClassroomAttendanceEnum.Invalid:
    default:
      return {text: 'Registered', color: 'gray'};
  }
};

export const CourseClassroomAttendeeResultPill = (
  result?: CourseClassroomAttendeeResultEnum
): PillProps => {
  switch (result) {
    case CourseClassroomAttendeeResultEnum.Passed:
      return {text: 'Passed', color: 'green'};
    case CourseClassroomAttendeeResultEnum.Failed:
      return {text: 'Failed', color: 'red'};
    case CourseClassroomAttendeeResultEnum.Waived:
      return {text: 'Waived', color: 'blue'};
    case CourseClassroomAttendeeResultEnum.Pending:
    case undefined:
    case CourseClassroomAttendeeResultEnum.Invalid:
    default:
      return {text: 'Pending', color: 'gray'};
  }
};

export const CourseAssignmentStatePill = (
  state?: CourseAssignmentStateEnum
): PillProps => {
  switch (state) {
    case CourseAssignmentStateEnum.Pending:
      return {text: 'In-progress', color: 'blue'};
    case CourseAssignmentStateEnum.Complete:
      return {text: 'Complete', color: 'green'};
    case CourseAssignmentStateEnum.Locked:
      return {text: 'Locked', color: 'red'};
    case CourseAssignmentStateEnum.Open:
      return {text: 'Open', color: 'yellow'};
    case undefined:
    case CourseAssignmentStateEnum.Invalid:
    default:
      break;
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};
export const CourseAssignmentEnrollmentPill = (active?: boolean): PillProps => {
  switch (active) {
    case true:
      return {text: 'Active Assignment', color: 'green'};
    case false:
      return {text: 'Inactive Assignment', color: 'yellow'};
    case undefined:
    default:
      break;
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const CourseAssignmentTypePill = (isRenewal?: boolean): PillProps => {
  if (isRenewal === true) {
    return {text: 'Renewal', color: 'blue'};
  }

  if (isRenewal === false) {
    return {text: 'First-Time', color: 'green'};
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const AssetTypePills = (isLeaf?: boolean): PillProps => {
  if (isLeaf === true) {
    return {
      text: 'Physical Asset',
      color: 'blue',
    };
  }

  if (isLeaf === false) {
    return {
      text: 'Asset Group',
      color: 'grey',
    };
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const TaskDefinitionStateEnumPill = (
  status: TaskDefinitionStateEnum
): PillProps => {
  switch (status) {
    case TaskDefinitionStateEnum.Draft:
      return {text: 'Draft', color: 'yellow'};
    case TaskDefinitionStateEnum.Published:
      return {text: 'Published', color: 'blue'};
    case TaskDefinitionStateEnum.Completed:
      return {text: 'Completed', color: 'green'};
    default:
      break;
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const RiskAssessmentCodeAreaPill = (
  area: RiskAssessmentCodeAreaEnum
): PillProps => {
  switch (area) {
    case RiskAssessmentCodeAreaEnum.HumanResources:
      return {text: 'Area: Human Resources', color: 'blue'};
    case RiskAssessmentCodeAreaEnum.Operations:
      return {text: 'Area: Operations', color: 'yellow'};
    case RiskAssessmentCodeAreaEnum.Quality:
      return {text: 'Area: Quality', color: 'green'};
    case RiskAssessmentCodeAreaEnum.Safety:
      return {text: 'Area: Safety', color: 'red'};
    default:
      return {text: 'Invalid', color: 'red'};
  }
};

export const InspectionStateEnumPill = (
  state?: InspectionStateEnum
): PillProps => {
  switch (state) {
    case InspectionStateEnum.Draft:
      return {text: 'Draft', color: 'yellow'};
    case InspectionStateEnum.InProgress:
      return {text: 'In Progress', color: 'blue'};
    case InspectionStateEnum.Completed:
      return {text: 'Completed', color: 'green'};
    case InspectionStateEnum.Scheduled:
      return {text: 'Scheduled', color: 'cyan'};
    case undefined:
    case InspectionStateEnum.Invalid:
    default:
      break;
  }

  return {
    text: 'Invalid',
    color: 'red',
  };
};

export const ComplianceStateEnumPill = (
  state?: ComplianceStateEnum
): PillProps => {
  switch (state) {
    case ComplianceStateEnum.Pass:
      return {text: 'Pass', color: 'green'};
    case ComplianceStateEnum.Fail:
      return {text: 'Fail', color: 'red'};
    case ComplianceStateEnum.NoAccess:
      return {text: 'No Access', color: 'blue'};
    case undefined:
    case ComplianceStateEnum.Invalid:
    default:
      return {text: 'Not Inspected', color: 'gray'};
  }
};

// Reverted = data correction, not remediation — own label.
export const DeficiencyStatePill = (
  status?: DeficiencyStatusEnum,
  resolutionSource?: DeficiencyResolutionSourceEnum
): PillProps => {
  if (status === DeficiencyStatusEnum.Open) {
    return {text: 'Open', color: 'red'};
  }

  if (status === DeficiencyStatusEnum.Resolved) {
    if (resolutionSource === DeficiencyResolutionSourceEnum.Reverted) {
      return {text: 'Reverted', color: 'gray'};
    }
    return {text: 'Resolved', color: 'green'};
  }

  return {text: 'Invalid', color: 'red'};
};

export const SafetyEventStatePill = (
  state: SafetyEventStateEnum
): PillProps => {
  switch (state) {
    case SafetyEventStateEnum.Assemble:
      return {text: 'Assemble', color: 'yellow'};
    case SafetyEventStateEnum.Published:
      return {text: 'Published', color: 'green'};
    default:
      return {text: 'Invalid', color: 'red'};
  }
};
